import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getReport } from "../lib/api";
import { CliBlock } from "../components/CliBlock";
import {
  Waves, Printer, ArrowLeft, CheckCircle2, AlertTriangle, Compass, Check, Route,
} from "lucide-react";

export default function ReportView() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getReport(shareId).then(setReport).catch(() => setError("Report not found."));
  }, [shareId]);

  if (error) return <div className="min-h-screen bg-background text-zinc-400 flex items-center justify-center">{error}</div>;
  if (!report) return <div className="min-h-screen bg-background text-zinc-500 font-mono flex items-center justify-center">Loading report…</div>;

  const isMigration = report.kind === "migration";
  const content = isMigration ? report.migration : report.symptom;
  const steps = content.steps || content.phases || [];
  const done = new Set(report.completed_steps || []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar (hidden when printing) */}
      <div className="print:hidden border-b border-zinc-800 bg-zinc-950 flex items-center justify-between px-6 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <button onClick={() => navigate("/reports")} data-testid="report-back-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Reports
        </button>
        <button onClick={() => window.print()} data-testid="report-print-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
          <Printer className="h-4 w-4" /> Export / Print
        </button>
      </div>

      <div className="max-w-3xl mx-auto p-8">
        {/* Letterhead */}
        <div className="flex items-center gap-3 pb-6 border-b border-zinc-800">
          {report.logo_url ? (
            <img src={report.logo_url} alt="logo" className="h-11 w-11 object-contain rounded bg-white/5 border border-zinc-800" data-testid="report-logo" />
          ) : (
            <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center">
              <Waves className="h-5 w-5 text-white" />
            </div>
          )}
          <div>
            {report.site_name ? (
              <>
                <div className="font-heading font-extrabold text-white tracking-tight text-lg" data-testid="report-site-name">{report.site_name}</div>
                <div className="text-[11px] text-zinc-500">UC Engineer Pro — Troubleshooting Report</div>
              </>
            ) : (
              <div className="font-heading font-extrabold text-white tracking-tight">UC Engineer Pro — Troubleshooting Report</div>
            )}
            <div className="text-[11px] text-zinc-500 font-mono">Generated {new Date(report.created_at).toLocaleString()} · ref {report.share_id}</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
            <Route className="h-4 w-4" /> {isMigration ? "Cloud Migration" : "Diagnostic"}
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-white mt-2">{content.title}</h1>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{report.symptom_category} · {report.symptom_severity}</p>
          <p className="text-zinc-400 mt-3 leading-relaxed">{content.summary}</p>
        </div>

        {report.notes && (
          <div className="mt-6 rounded-md border border-zinc-800 bg-zinc-900 p-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Engineer Notes</div>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{report.notes}</p>
          </div>
        )}

        <div className="mt-8 space-y-5">
          {steps.map((step, idx) => (
            <div key={step.id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <div className="flex items-center gap-3">
                <div className={`h-7 w-7 shrink-0 rounded-md font-mono text-sm font-bold flex items-center justify-center border ${done.has(step.id) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-blue-600/15 border-blue-600/30 text-blue-400"}`}>
                  {done.has(step.id) ? <Check className="h-4 w-4" /> : idx + 1}
                </div>
                <div>
                  <div className="font-heading font-bold text-white">{step.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{step.layer}{done.has(step.id) ? " · completed" : ""}</div>
                </div>
              </div>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{step.description}</p>

              {step.cli_commands?.length > 0 && (
                <div className="mt-3"><CliBlock commands={step.cli_commands} /></div>
              )}

              {step.navigation?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {step.navigation.map((nav, i) => (
                    <div key={i} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                      <div className="flex items-start gap-2">
                        <Compass className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold">{nav.system}</div>
                          <div className="font-mono text-[13px] text-zinc-200 mt-1">{nav.path}</div>
                          {nav.note && <div className="text-xs text-zinc-500 mt-1">{nav.note}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step.good_output && (
                <div className="mt-3 rounded-md bg-zinc-950 border border-zinc-800 border-l-4 border-l-emerald-500 p-3">
                  <div className="flex items-center gap-2 text-emerald-400 text-[11px] uppercase tracking-wider font-semibold"><CheckCircle2 className="h-4 w-4" /> Good Output</div>
                  <p className="text-sm text-zinc-300 mt-1.5">{step.good_output}</p>
                </div>
              )}
              {step.bad_signs && (
                <div className="mt-2 rounded-md bg-zinc-950 border border-zinc-800 border-l-4 border-l-red-500 p-3">
                  <div className="flex items-center gap-2 text-red-400 text-[11px] uppercase tracking-wider font-semibold"><AlertTriangle className="h-4 w-4" /> Warning Signs</div>
                  <p className="text-sm text-zinc-300 mt-1.5">{step.bad_signs}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 pt-4 border-t border-zinc-800 text-[11px] text-zinc-600 font-mono text-center">
          UC Engineer Pro · Field Diagnostics Quick Reference
        </div>
      </div>
    </div>
  );
}
